import apiClient from './api';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface SignupRequest {
  userName: string;
  phoneNumber: string;
  emailId: string;
  password: string;
}

export interface ValidateUserResponse {
  success: boolean;
  isMobile?: boolean;
  isEmail?: boolean;
  newUser?: boolean;
  isPartners?: boolean;
  error?: string;
  message?: string;
  otpCooldownSeconds?: number;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  token?: string;
  user?: any;
  redirectUrl?: string;
}

export interface ProfileUpdateRequest {
  userName?: string;
  emailId?: string;
  phoneNumber?: string;
}

export interface CurrentUserResponse {
  success: boolean;
  message?: string;
  user?: any;
  redirectUrl?: string;
}

export interface SocialAuthConfig {
  success: boolean;
  googleEnabled: boolean;
  facebookEnabled: boolean;
  googleClientId?: string | null;
  facebookAppId?: string | null;
}

class AuthService {
  async validateUser(username: string): Promise<ValidateUserResponse> {
    const response = await apiClient.post('/api/validate-user', username, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  }

  async login(username: string, password: string): Promise<AuthResponse> {
    const response = await apiClient.post('/api/auth/login', {
      username,
      password
    });
    
    // Token is now stored in httpOnly cookie by the server
    // User info will be stored in Redux store, not localStorage
    
    return response.data;
  }

  async socialLogin(provider: string, credential: string): Promise<AuthResponse> {
    const response = await apiClient.post('/api/auth/social', {
      provider,
      credential,
    });
    return response.data;
  }

  async getSocialAuthConfig(): Promise<SocialAuthConfig> {
    const response = await apiClient.get('/api/auth/social/config');
    return response.data;
  }

  async signup(data: SignupRequest): Promise<AuthResponse> {
    const response = await apiClient.post('/api/auth/signup', data);
    
    // Token is now stored in httpOnly cookie by the server
    // User info will be stored in Redux store, not localStorage
    
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      console.log('Making logout API call to /api/auth/logout'); // Debug log
      const response = await apiClient.post('/api/auth/logout');
      console.log('Logout response:', response); // Debug log
      // Token cookie is cleared by the server
      // User will be cleared from Redux store
    } catch (error: any) {
      console.error('Logout API error:', error);
      // Re-throw to let the caller handle it
      throw error;
    }
  }

  async getCurrentUser(): Promise<CurrentUserResponse> {
    const response = await apiClient.get('/api/auth/me');
    return response.data;
  }

  async updateCaptainVoicePreference(enabled: boolean): Promise<CurrentUserResponse> {
    const response = await apiClient.patch('/api/auth/preferences', {
      captainVoiceEnabled: enabled,
    });
    return response.data;
  }

  async updateProfile(data: ProfileUpdateRequest): Promise<CurrentUserResponse> {
    const response = await apiClient.patch('/api/auth/profile', data);
    return response.data;
  }

  async forgotPassword(email: string): Promise<AuthResponse> {
    const response = await apiClient.post('/api/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(token: string, password: string): Promise<AuthResponse> {
    const response = await apiClient.post('/api/auth/reset-password', { token, password });
    return response.data;
  }

  /**
   * Check if user has a valid token cookie
   * Note: We can't read httpOnly cookies from JavaScript, so we check by making an API call
   */
  async checkAuthToken(): Promise<boolean> {
    try {
      const response = await this.getCurrentUser();
      return response.success && !!response.user;
    } catch (error) {
      return false;
    }
  }

  /**
   * Partner Setup APIs
   */
  async savePartnerName(
    userName: string,
    phoneCountryCode?: string,
    phoneNumber?: string
  ): Promise<PartnerSetupResponse> {
    const formData = new URLSearchParams();
    formData.append('userName', userName);
    if (phoneCountryCode) {
      formData.append('phoneCountryCode', phoneCountryCode);
    }
    if (phoneNumber) {
      formData.append('phoneNumber', phoneNumber);
    }
    
    const response = await apiClient.post('/partner/setup/saveName', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }

  async savePartnerRestaurant(
    restaurantName: string,
    slug: string,
    city: string,
    country: string,
    acceptTerms: boolean,
    fssaiNumber?: string
  ): Promise<PartnerSetupResponse> {
    const formData = new URLSearchParams();
    formData.append('restaurantName', restaurantName);
    formData.append('slug', slug);
    formData.append('city', city);
    formData.append('country', country);
    formData.append('acceptTerms', acceptTerms ? 'true' : 'false');
    if (fssaiNumber) {
      formData.append('fssaiNumber', fssaiNumber);
    }
    
    const response = await apiClient.post('/partner/setup/saveRestaurant', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }

  async sendPartnerPhoneOtp(): Promise<PartnerPhoneOtpResponse> {
    const response = await apiClient.post('/partner/setup/sendPhoneOtp', new URLSearchParams(), {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }

  async verifyPartnerPhoneOtp(otp: string): Promise<PartnerPhoneOtpResponse> {
    const formData = new URLSearchParams();
    formData.append('otp', otp);
    const response = await apiClient.post('/partner/setup/verifyPhoneOtp', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }

  async checkPartnerSlug(slug: string): Promise<SlugCheckResponse> {
    // Use URLSearchParams for form-encoded data
    const formData = new URLSearchParams();
    formData.append('slug', slug);
    
    const response = await apiClient.post('/partner/setup/checkSlug', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }

  async acceptPartnerLegal(acceptTerms: boolean): Promise<PartnerSetupResponse> {
    const formData = new URLSearchParams();
    formData.append('acceptTerms', acceptTerms ? 'true' : 'false');
    const response = await apiClient.post('/partner/legal-consent/accept', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  }
}

export interface PartnerSetupResponse {
  success: boolean;
  message?: string;
  redirectUrl?: string;
  /** Server-side diagnostic detail (exception type + message); not shown to the user. */
  error?: string;
  phoneOtpRequired?: boolean;
}

export interface PartnerPhoneOtpResponse {
  success: boolean;
  message?: string;
  otpCooldownSeconds?: number;
  maskedPhone?: string;
  otpStillValid?: boolean;
}

export interface SlugCheckResponse {
  available: boolean;
  message: string;
}

export default new AuthService();
